import isEmpty from 'lodash/isEmpty';
import { gettext } from '@eventbrite/i18n';
import {
    cardTypeForNumber,
    isValidCardNumberForCardType,
} from '@eventbrite/eds-utils';

import {
    shouldPerform3DS2Validation,
    threeDomainSecurePromise,
} from '../threeDomainSecure';

import { createNewVaultInstrument as createNewVaultInstrumentApi } from '../../api/users';

import { handleAnalyticsTokenOnError } from '../tokens';
import {
    PAYMENT_METHODS,
    PAYMENT_METHOD_TYPES_SUPPORTING_CREDIT_CARD_INSTRUMENT,
    PAYMENT_METHOD_TYPES_SUPPORTING_MAESTRO_BANCONTACT_INSTRUMENT,
    CREDIT_CARD_BASED_PAYMENT_METHODS,
    CHECKOUT_FORM_NAME,
} from '../../constants';

import {
    isAllInfoRequiredToSavePaymentMethodAvailable,
    clearPaymentMethods as clearPaymentMethodsApi,
    formatCreditCardInstrument,
    placeOrder as placeOrderApi,
    placeOrderRetrying as placeOrderRetryingApi,
    savePaymentMethods as savePaymentMethodsApi,
    saveOrder as saveOrderApi,
} from '../../api/order';

import { CustomSubmissionError } from '@eventbrite/redux-form-tools';
import {
    getAvailablePaymentMethods,
    getIsNosSourceOfTruth,
    getOrderId,
} from '../../selectors/order/typedOrderSelectors';
import {
    getAttendeeDeliveryMethodsPayloadForPlacingOrder,
    getEmailConfiguration,
    getTotalCostsFromGrossMajorValue,
    isPaidOrder as isOrderToBePaid,
} from '../../selectors/order';
import { getConstrainedPaymentMethods } from '../../selectors/tickets/pureSelectors';
import { getCurrency, getEventCountryCode } from '../../selectors/event';
import { getSurvey } from '../../selectors/survey';
import { getUserId, isUserLoggedIn } from '../../selectors/user';

import { cleanFormData } from '../../containers/checkout/utils';

import { _handleUpdateOrder, _handleUpdateOrderToken } from './common';
import { setOrderUpdatingAction, setHasRetryPaymentError } from './actions';
import { OrderActionType } from './types';
import { updateOrderAction } from './updateOrderAction';
import { clearPaymentMethods } from './paymentMethods';
import { getFeatureFlags } from '../../selectors/app';
import {
    ADD_PAYMENT_INFO,
    triggerTrackingPixelAction,
} from '../pixelAnalytics';

import {
    getPostMessageActionParamsFromState,
    handleOrderError,
    userIsStoringNewCreditCard,
    checkIfUserWasLoggedIn,
} from './utils';
import { updateBackgroundTimer } from '../timer';

import {
    getSaveOrderFormData,
    getSavePaymentMethodsFormData,
    getPlaceOrderFormData,
} from './formData';

import { checkTaxExemptionApplies } from '../taxExemptions';
import { isOrderFormSplittedInTwoPages } from '../../selectors/survey';
import { logEvent } from '@eventbrite/statsig';
import { getRateFromOrganizerQuestionsAnswered } from '../../utils/surveys';
import { STATSIG_EVENT_NAMES } from '../../constants/statsig';
import { handleOrderErrorInTicketSelectionPage } from '../../actions/order/utils';
import { isTicketSelectionFullSizePage } from '../../selectors/routing';
import { getEnablePlaceOrderWithoutAnsweringMandatoryQuestions } from '../../selectors/postCheckoutMandatoryQuestions';

// TODO: EB-17141 This is exported only for tests, we should not export it
/**
 * @deprecated TODO: EB-17141 This is exported only for tests, we should not export it
 */
export const placeOrderPromise = ({
    dispatch,
    getState,
    paymentData = {},
    countryCode,
}) => {
    const state = getState();
    const body = getEmailConfiguration(state);
    const orderId = getOrderId(state);
    const currency = getCurrency(state);
    const isNOS = getIsNosSourceOfTruth(state);

    // TODO: EB-130249 Don't use placeOrderRetryingApi after race condition is fixed
    const placeOrderApiPromise = getFeatureFlags(state)
        .launchEmbeddedCheckoutRetryOnPlaceError
        ? placeOrderRetryingApi(orderId, body, {
              currency,
              paymentMethod: paymentData.paymentMethod,
          })
        : placeOrderApi(orderId, body);

    return placeOrderApiPromise
        .then((placeOrderResponse) => {
            const state = getState();

            if (placeOrderResponse.response) {
                _handleUpdateOrder(dispatch, placeOrderResponse.response);
                _handleUpdateOrderToken(dispatch, placeOrderResponse.headers);
            }

            dispatch({
                type: OrderActionType.updateOrder,
                payload: {
                    isPlaced: true,
                },
            });

            // Check if a new user was created
            if (!isUserLoggedIn(state)) {
                checkIfUserWasLoggedIn()(dispatch, getState);
            }

            // Store the user's credit card to their "wallet" if they selected to save it.
            if (userIsStoringNewCreditCard(paymentData) && !isNOS) {
                const userId = getUserId(state);
                const currency = getCurrency(state);

                const { instrument, billingInformation } =
                    formatCreditCardInstrument({
                        countryCode,
                        payment: paymentData.payment,
                        currency,
                        instrumentType: PAYMENT_METHODS.CREDIT,
                    });

                createNewVaultInstrumentApi(
                    userId,
                    instrument,
                    billingInformation,
                );
            }

            return placeOrderResponse;
        })
        .catch((error) => {
            handleAnalyticsTokenOnError(dispatch, getState);

            if (isTicketSelectionFullSizePage(getState())) {
                handleOrderErrorInTicketSelectionPage(dispatch);
            }

            handleOrderError(dispatch, error);
            dispatch(updateBackgroundTimer());
        });
};

// TODO: EB-17141 This is exported only for tests, we should not export it
/**
 * @deprecated TODO: EB-17141 This is exported only for tests, we should not export it
 */
export const savePaymentMethods = async ({
    dispatch,
    getState,
    formData,
    clearPaymentMethodsWhenMissingInfo = false,
}) => {
    const state = getState();
    const isPaidOrder = isOrderToBePaid(state);
    const isNOS = getIsNosSourceOfTruth(state);

    if (!isPaidOrder) return;

    const orderId = getOrderId(state);
    const currency = getCurrency(state);
    const countryCode = getEventCountryCode(state);
    const paymentMethodsFormData = getSavePaymentMethodsFormData({
        formData,
        state,
    });

    const hasAllInfoRequiredToSavePaymentMethods =
        isAllInfoRequiredToSavePaymentMethodAvailable({
            ...paymentMethodsFormData,
            countryCode,
            currency,
        });

    const shouldClearPaymentMethods =
        clearPaymentMethodsWhenMissingInfo &&
        !hasAllInfoRequiredToSavePaymentMethods;

    let responsePromise;

    if (shouldClearPaymentMethods) {
        responsePromise = clearPaymentMethodsApi(orderId);
    } else {
        // EB-163838 Workaround to update the fees of Paypal payment method. The save petition
        // is returning 400 (nonce attribute missing), so we need to do a clear first.
        const isNonceBraintree =
            paymentMethodsFormData.paymentMethod ===
            PAYMENT_METHODS.NONCE_BRAINTREE;

        if (isNonceBraintree) {
            await dispatch(clearPaymentMethods());
        }

        responsePromise = savePaymentMethodsApi({
            orderId,
            countryCode,
            paymentData: paymentMethodsFormData,
            currency,
            isNOS,
        });
    }

    try {
        const response = await responsePromise;
        const updatedOrder = response.response;

        // In GST taxes we don't get `updatedOrder.costs.tax` back when the order is exempted.
        // We keep the previous value in `state.order.tax.taxBeforeExemption` in order to show the GST tax as exempted in the order summary.
        // We set `updatedOrder.costs.tax.taxBeforeExemption` so it overwrites `state.order.costs.tax.taxBeforeExemption` when we dispatch `updateOrder` action.
        // If we always set `updatedOrder.order.costs.tax.taxBeforeExemption` regardless `updatedOrder.costs.tax` then we will always be in the "order is exempted" status.
        // That's why we only take `taxBeforeExemption` from state when  there are taxes coming back from Order Service
        if (
            state.order &&
            state.order.costs &&
            state.order.costs.tax &&
            updatedOrder.costs &&
            updatedOrder.costs.tax
        ) {
            updatedOrder.costs.tax.taxBeforeExemption =
                state.order.costs.tax.taxBeforeExemption;
        }
        dispatch(updateOrderAction(updatedOrder));

        return response;
    } finally {
        await dispatch(checkTaxExemptionApplies());
    }
};

// Promise in charge of saving the payment method to the order
const saveOrder = async ({ dispatch, getState, formData }) => {
    const state = getState();
    const orderId = getOrderId(state);
    const deliveryMethods =
        getAttendeeDeliveryMethodsPayloadForPlacingOrder(state);

    const survey = getSurvey(getState());
    const optionalQuestionAnswerRatio = getRateFromOrganizerQuestionsAnswered(
        survey,
        formData,
        CHECKOUT_FORM_NAME,
    );

    if (!isOrderFormSplittedInTwoPages(state) && optionalQuestionAnswerRatio) {
        logEvent(
            STATSIG_EVENT_NAMES.CHECKOUT_STEP_SUCCESS_ORGANIZER_QUESTIONS_RATIO,
            undefined,
            optionalQuestionAnswerRatio,
        );
    }

    await saveOrderApi({
        orderId,
        formData: getSaveOrderFormData({ formData, state }),
        deliveryMethods,
        isPostCheckoutQuestionsEnabled:
            getEnablePlaceOrderWithoutAnsweringMandatoryQuestions(state),
    });

    dispatch(triggerTrackingPixelAction(ADD_PAYMENT_INFO));

    await savePaymentMethods({ dispatch, getState, formData });
};

/**
 * Returns the instruments types of supported available payment methods.
 *
 * @param {Object} params
 * @param {Array<{type: string, instrumentType: Array<{type: string}>}>} params.availablePaymentMethods
 * @param {Record<string, boolean>} params.supportedPaymentMethodTypes
 * @returns {Array<{type: string, variants: Array<string>}>}
 */
const getSupportedPaymentInstruments = ({
    availablePaymentMethods,
    supportedPaymentMethodTypes,
}) =>
    availablePaymentMethods
        .filter((method) => supportedPaymentMethodTypes[method.type])
        .flatMap((method) => method.instrumentTypes);

/**
 * Returns the credit card instrument if available.
 *
 * @param {Array<{type: string, instrumentType: Array<{type: string}>}>} availablePaymentMethods
 * @returns {{type: string, variants: Array<string>} | undefined}
 */
const getCreditCardInstrument = (availablePaymentMethods) =>
    getSupportedPaymentInstruments({
        availablePaymentMethods,
        supportedPaymentMethodTypes:
            PAYMENT_METHOD_TYPES_SUPPORTING_CREDIT_CARD_INSTRUMENT,
    }).find((instrument) => CREDIT_CARD_BASED_PAYMENT_METHODS[instrument.type]);

/**
 * Returns the bancontact instrument if available.
 *
 * @param {Array<{type: string, instrumentType: Array<{type: string}>}>} availablePaymentMethods
 * @returns {{type: string, variants: Array<string>} | undefined}
 */
const getBancontactInstrument = (availablePaymentMethods) =>
    getSupportedPaymentInstruments({
        availablePaymentMethods,
        supportedPaymentMethodTypes:
            PAYMENT_METHOD_TYPES_SUPPORTING_MAESTRO_BANCONTACT_INSTRUMENT,
    }).find(
        (instrument) => instrument.type === PAYMENT_METHODS.MAESTRO_BANCONTACT,
    );

/**
 * @param {object} formData
 * @param {object} [formData.payment]
 * @param {string} [formData.payment.creditCardNumber]
 * @returns {string|null}
 */
const getCreditCardNumberFromFormData = (formData) =>
    formData.payment?.creditCardNumber ?? null;

/**
 * Returns a localized error message if provided credit card is not allowed because it is not included in constrained
 * payment methods.
 *
 * @param {object} formData
 * @param {object} [formData.payment]
 * @param {string} [formData.payment.creditCardNumber]
 * @param {*} state
 * @returns {string|null} `null` when credit card is allowed, localized error message otherwise.
 */
const getPaymentMethodContraintsUnsatisfiedErrorMessage = (formData, state) => {
    const creditCardNumber = getCreditCardNumberFromFormData(formData);
    const cardType = cardTypeForNumber(creditCardNumber);
    const constrainedPaymentMethods = getConstrainedPaymentMethods(state);

    const allowedPaymentMethods = !isEmpty(constrainedPaymentMethods)
        ? constrainedPaymentMethods
        : [cardType];

    return allowedPaymentMethods.includes(cardType)
        ? null
        : gettext(
              'Credit card does not match required card type for this ticket',
          );
};

/**
 * Returns a localized error message if provided credit card is not allowed because it's card type is not included in
 * the allowlist.
 *
 * @param {object} formData
 * @param {object} [formData.payment]
 * @param {string} [formData.payment.creditCardNumber]
 * @param {*} state
 * @returns {string|null} `null` when credit card type is in the allowlist, localized error message otherwise.
 */
const getPaymentMethodNotInAllowlistErrorMessage = (formData, state) => {
    const creditCardNumber = getCreditCardNumberFromFormData(formData);
    if (!creditCardNumber) return null;

    const cardType = cardTypeForNumber(creditCardNumber);

    const availablePaymentMethods = getAvailablePaymentMethods(state);
    const creditCardInstrument = getCreditCardInstrument(
        availablePaymentMethods,
    );
    if (!creditCardInstrument) return null;

    const bancontactInstrument = getBancontactInstrument(
        availablePaymentMethods,
    );

    const allowedCards = bancontactInstrument
        ? [...creditCardInstrument.variants, PAYMENT_METHODS.MAESTRO_BANCONTACT]
        : creditCardInstrument.variants;

    const isCardTypeAllowed = allowedCards.includes(cardType);
    const isCardNumberValid = isValidCardNumberForCardType(
        creditCardNumber,
        cardType.toLowerCase(),
    );
    const isCardDisallowed = !isCardTypeAllowed && isCardNumberValid;
    if (!isCardDisallowed) return null;

    const isOnlyAmexAllowed =
        allowedCards.length === 1 && allowedCards.includes('AMEX');

    return isOnlyAmexAllowed
        ? gettext('Only American Express cards are supported for this event.')
        : gettext('The type of card you used is not supported.');
};

/**
 * Returns a localized error message if provided credit card is not allowed.
 *
 * @param {object} formData
 * @param {object} [formData.payment]
 * @param {string} [formData.payment.creditCardNumber]
 * @param {*} state
 * @returns {string|null} `null` when credit card is allowed, localized error message otherwise.
 */
const getCreditCardNotAllowedErrorMessage = (formData, state) => {
    const creditCardNumber = getCreditCardNumberFromFormData(formData);
    if (!creditCardNumber) return null;

    const availablePaymentMethods = getAvailablePaymentMethods(state);
    const creditCardInstrument = getCreditCardInstrument(
        availablePaymentMethods,
    );
    if (!creditCardInstrument) return null;

    return (
        getPaymentMethodContraintsUnsatisfiedErrorMessage(formData, state) ||
        getPaymentMethodNotInAllowlistErrorMessage(formData, state)
    );
};

const getPromiseWrappingCreditCardErrorMessage = (
    creditCardErrorMessage,
    dispatch,
) =>
    new Promise(() => {
        throw new CustomSubmissionError(
            {
                payment: {
                    creditCardNumber: creditCardErrorMessage,
                },
                _error: gettext('Please correct the highlighted fields below'),
            },
            {},
        );
    }).catch((error) => handleOrderError(dispatch, error));

/**
 * @param {PaymentMethodType} newPaymentMethod
 * @param {PaymentMethodType} previousPaymentMethod
 */
export const updateOrderPaymentMethod =
    (newPaymentMethod, previousPaymentMethod) => async (dispatch, getState) => {
        if (newPaymentMethod === previousPaymentMethod) return;

        const state = getState();
        const survey = getSurvey(getState());
        const formDataCleaned = cleanFormData(
            state.form.checkoutForm.values,
            survey,
        );

        dispatch(setOrderUpdatingAction(true));

        try {
            await savePaymentMethods({
                dispatch,
                getState,
                formData: formDataCleaned,
                clearPaymentMethodsWhenMissingInfo: true,
            });
        } finally {
            dispatch(setOrderUpdatingAction(false));
        }
    };

/**
 * Saves and places the user's order. This function will choose the correct flow and state updates
 * based on the user's selected payment method and information they have entered.
 */
export const placeOrder =
    ({ formData }) =>
    (dispatch, getState) => {
        const state = getState();
        const orderId = getOrderId(state);
        const { eventId, widgetParentUrl } =
            getPostMessageActionParamsFromState(state);

        // First check if a CC number was used and if so is it one of the allowed types.
        const creditCardNotAllowedErrorMessage =
            getCreditCardNotAllowedErrorMessage(formData, state);
        if (creditCardNotAllowedErrorMessage) {
            return {
                saveAndPlacePromise: getPromiseWrappingCreditCardErrorMessage(
                    creditCardNotAllowedErrorMessage,
                    dispatch,
                ),
            };
        }
        dispatch(setHasRetryPaymentError(false));

        const saveAndPlacePromise = saveOrder({
            dispatch,
            getState,
            formData,
        }).then(async () => {
            await dispatch(checkTaxExemptionApplies());

            const countryCode = getEventCountryCode(state);
            const paymentData = getPlaceOrderFormData({ formData, state });

            if (shouldPerform3DS2Validation({ state, paymentData })) {
                const currency = getCurrency(state);
                const amount = getTotalCostsFromGrossMajorValue(state);
                const userCountry = state.app.locale;

                await threeDomainSecurePromise({
                    dispatch,
                    savePaymentMethods: savePaymentMethodsApi,
                    handleOrderError,
                    paymentData,
                    currency,
                    countryCode,
                    amount,
                    orderId,
                    eventId,
                    userCountry,
                });
            }

            return placeOrderPromise({
                dispatch,
                getState,
                paymentData,
                countryCode,
            });
        });

        return {
            dispatch,
            eventId,
            orderId,
            widgetParentUrl,
            formData,
            saveAndPlacePromise,
        };
    };
